import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { SITE } from "@/lib/site-config";
import { useSiteContent } from "@/lib/site-content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const scrollToForm = () => {
  const el = document.getElementById("lead-form");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Fire-and-forget bestseller tracking, then open the checkout in a new tab.
const trackClick = (product) => {
  try {
    fetch(`${BACKEND_URL}/api/track/purchase-click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product }),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
};

const buy = (product, url) => {
  trackClick(product);
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

// Products with a single card => single Stripe checkout key.
const DIRECT_PURCHASE_KEY = { kochbuch: "kochbuch", ernaehrungsplan: "ernaehrungsplan" };

// Trainingsplan selector: "Modell X Tage/Woche" -> Stripe "Trainingsplan X".
const TRAINING_OPTIONS = [
  { key: "trainingsplan_2", label: "Trainingsplan 2 Tage" },
  { key: "trainingsplan_3", label: "Trainingsplan 3 Tage" },
  { key: "trainingsplan_4", label: "Trainingsplan 4 Tage" },
  { key: "trainingsplan_5", label: "Trainingsplan 5 Tage" },
  { key: "trainingsplan_6", label: "Trainingsplan 6 Tage" },
];

const ctaClass = (highlight) =>
  `inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition-colors ${
    highlight ? "text-[#E8E5D0] hover:text-[#F3F1E8]" : "text-[#F3F1E8] hover:text-[#E8E5D0]"
  }`;

const ServiceCard = ({ s, i, links, prices }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const isTraining = s.id === "trainingsplaene";
  const directKey = DIRECT_PURCHASE_KEY[s.id];

  // Resolve the displayed price from backend-managed prices (falls back to config).
  let displayPrice = s.price;
  if (directKey && prices[directKey]) displayPrice = prices[directKey];
  if (isTraining) displayPrice = prices[selected || "trainingsplan_2"] || s.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: i * 0.05 }}
      whileHover={{ y: -4 }}
      className={`group relative rounded-2xl p-6 border transition-colors overflow-hidden ${
        s.highlight
          ? "bg-[#E8E5D0]/[0.06] border-[#E8E5D0]/30 hover:border-[#E8E5D0]/60"
          : "bg-[#4E5A3B]/60 border-[rgba(255,255,255,0.14)] hover:border-[rgba(255,255,255,0.28)]"
      }`}
      data-testid={`service-card-${s.id}`}
    >
      {s.highlight && (
        <div className="absolute -top-px -right-px">
          <div className="bg-[#E8E5D0] text-[#3A4230] text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-bl-xl flex items-center gap-1">
            <Sparkles size={11} /> {s.tag}
          </div>
        </div>
      )}
      {!s.highlight && (
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A8A58F] mb-3">
          {s.tag}
        </div>
      )}

      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-2xl font-black text-[#F3F1E8] tracking-tight">
          {s.title}
        </h3>
      </div>
      <div
        className="mt-1 font-display text-3xl font-black lime-gradient-text transition-all"
        data-testid={`service-price-${s.id}`}
      >
        {displayPrice}
      </div>

      <p className="mt-4 text-sm text-[#C9C6B4] leading-relaxed">{s.desc}</p>

      <ul className="mt-5 space-y-2.5">
        {s.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-sm text-[#E5E2D0]">
            <span className="mt-1 flex items-center justify-center w-4 h-4 rounded-full bg-[#E8E5D0]/20 border border-[#E8E5D0]/40 shrink-0">
              <Check size={10} className="text-[#E8E5D0]" />
            </span>
            {b}
          </li>
        ))}
      </ul>

      {isTraining ? (
        <div className="mt-6">
          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className={ctaClass(s.highlight)}
              data-testid={`service-cta-${s.id}`}
            >
              Trainingsplan wählen
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
          ) : (
            <div className="space-y-3" data-testid="training-selector">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A8A58F]">
                Wähle dein Modell
              </div>
              <div className="grid grid-cols-1 gap-2">
                {TRAINING_OPTIONS.map((o) => {
                  const active = selected === o.key;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setSelected(o.key)}
                      className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold border transition-colors text-left ${
                        active
                          ? "bg-[#E8E5D0] text-[#3A4230] border-[#E8E5D0]"
                          : "bg-[#4E5A3B]/50 text-[#E5E2D0] border-white/12 hover:border-white/25"
                      }`}
                      data-testid={`training-option-${o.key}`}
                    >
                      <span>{o.label}</span>
                      <span className="flex items-center gap-2">
                        <span className={active ? "text-[#3A4230] font-black" : "text-[#A8A58F]"}>
                          {prices[o.key] || ""}
                        </span>
                        {active && <Check size={14} />}
                      </span>
                    </button>
                  );
                })}
              </div>
              {selected && (
                <button
                  type="button"
                  onClick={() => buy(selected, links[selected])}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-full px-5 py-2.5 text-sm transition-colors"
                  data-testid="training-buy-btn"
                >
                  Jetzt kaufen · {prices[selected] || ""} <ArrowRight size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => (directKey ? buy(directKey, links[directKey]) : scrollToForm())}
          className={`mt-6 ${ctaClass(s.highlight)}`}
          data-testid={`service-cta-${s.id}`}
        >
          {s.cta || "Jetzt anfragen"}
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </button>
      )}
    </motion.div>
  );
};

const Services = () => {
  const { purchaseLinks = {}, purchasePrices = {} } = useSiteContent();

  return (
    <section
      id="services"
      className="relative py-20 md:py-32 border-t border-[#556340]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0] mb-4">
            Leistungen
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
            Wähle dein Setup.
            <br />
            <span className="text-[#A8A58F]">Vom Tool bis zum 1:1.</span>
          </h2>
          <p className="mt-5 text-[#C9C6B4] max-w-2xl">
            Vom Kochbuch über strukturierte Trainingspläne bis hin zu vollem 1:1
            Coaching – alles modular, alles aufeinander abgestimmt.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SITE.services.map((s, i) => (
            <ServiceCard key={s.id} s={s} i={i} links={purchaseLinks} prices={purchasePrices} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
